import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { X } from 'lucide-react';
import { attachmentFile, type ConversationAttachment, useConversationAttachments } from '../../../lib/conversation-attachments';
import { useAgentWorkspaceFiles, type AgentWorkspaceFileItem } from '../../../lib/agent-workspace-files';
import { workspaceFileRefOf } from '../../../lib/vfs-attachment';
import { fetchVfsFileContent } from '../../../lib/chat-files';
import { docViewerKind } from '../../../lib/doc-viewer';
import { openDocViewer } from '../../../lib/doc-viewer-presenter';
import { downloadArtifact } from '../../../lib/artifact-file';
import type { WorkspaceFile } from '../../../lib/workspace-files';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { buildFileActions, type FileActionCallbacks } from '../../../lib/file-actions';
import { effectiveContent } from '../../../lib/file-record';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { Card, IconButton, PageLoader, ScrollArea, Typography } from '../ui';
import { FilesFlatList } from '../files/FilesFlatList';
import type { FilesRow } from '../files/files-row';
import styles from './ChatFilesPanel.module.css';

const PAGE = 'chat_files';

/** The explorer's own file for a `vfsPath` attachment, else the attachment as its own bare file. Exact
 *  slug + name match — the vfsPath→name transform is a contract, and a looser basename match could
 *  collide across folders (the same rule `resolveVfsContent` applies). */
function resolveAttachment(attachment: ConversationAttachment, files: AgentWorkspaceFileItem[]) {
  const ref = attachment.vfsPath ? workspaceFileRefOf(attachment.vfsPath) : null;
  const match = ref && files.find((file) => file.workspaceSlug === ref.slug && file.name === ref.name);
  return match || attachmentFile(attachment);
}

// The per-chat Files/Media tab. A fixed overlay because this surface has no tab-strip/side-panel
// chrome to hook into yet — see orion-web/CLAUDE.md's three-surface-parity note.
//
// The list itself is the SAME one the Files page draws (`FilesFlatList` → `FilesTable` / `FilesGrid`, with
// the list/thumbnails switch), minus the browsing chrome a conversation has no answer for: no folders, since
// an attachment has no path, and no filter bar.
//
// TRACKED DIVERGENCE — no "go to message" here, where iOS/Android offer it in the row's "..." menu and jump
// the transcript to the message that sent the file. Both phones have the machinery (pendingTargetMessageId +
// ChatAutoScroll, which the orion:// deep-link uses). Web has none of it, and no older-page load at all — the
// transcript only ever holds the newest PAGE_LIMIT rows — so the target message often isn't loaded. Closing
// this means adding pagination first; it is a real gap, not an exception.
export function ChatFilesPanel({ conversationId, onClose }: { conversationId: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const router = useRouter();
  const basePath = useBasePath();
  const agent = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const { attachments, loading } = useConversationAttachments(conversationId);
  // The explorer's own files. An agent-authored attachment arrives with an empty `url` and only a
  // `vfsPath`, and the proto says clients resolve that path against the files they already hold — which
  // is what gives the row its thumbnail, size and writer instead of a bare type glyph.
  const filesState = useAgentWorkspaceFiles();
  const workspaceFiles = filesState.status === 'ready' ? filesState.items : [];

  // A workspace artifact's `attachmentFile` content is its `vfsPath` — an ADDRESS, not a body — so it has
  // to be resolved before the viewer or the download helper sees it. Skipping this handed the viewer the
  // literal `workspaces/…` string as the document, and `downloadArtifact` wrote that string to a file.
  const resolvedFile = async (attachment: ConversationAttachment, file: WorkspaceFile): Promise<WorkspaceFile> => {
    // Already a real file — the row resolved to the explorer's copy, which addresses its bytes through
    // `referenceUrl`. Re-reading the path here would cost a round trip to arrive at the same document.
    if (attachment.url || !attachment.vfsPath || file.referenceUrl || file.content) return file;
    return { ...file, content: (await fetchVfsFileContent(attachment.vfsPath)) ?? '' };
  };

  // The row keeps its attachment beside its file shape: the open/share helpers take the file, and resolving a
  // vfsPath needs the attachment. A chat attachment has no page of its own, so the row's link is the
  // conversation it came from — a middle-click lands back on the chat rather than on a 404.
  const rows: FilesRow[] = useMemo(
    () =>
      attachments.map((attachment, i) => ({
        key: `${attachment.messageId}:${attachment.fileName}:${i}`,
        item: resolveAttachment(attachment, workspaceFiles),
        href: router.asPath,
        as: router.asPath,
        basePath,
      })),
    [attachments, workspaceFiles, basePath, router.asPath],
  );
  // Keyed, not positional. The list sorts the rows before rendering, and while that sort keeps the same row
  // OBJECTS (so an index lookup happens to survive it), a click resolving its file through a position in a
  // separately ordered array is one refactor away from opening someone else's document.
  const attachmentByKey = useMemo(
    () => new Map(rows.map((row, i) => [row.key, attachments[i]])),
    [rows, attachments],
  );
  const attachmentOf = (row: FilesRow) => attachmentByKey.get(row.key);

  const open = (row: FilesRow) => {
    logPageClickBi({ page_name: PAGE, button_name: 'open_file', answer: row.item.name });
    // Every kind, including the ones with no preview — same modal, which says so and still offers
    // Download / Open in new tab.
    const attachment = attachmentOf(row);
    if (!attachment) return;
    void resolvedFile(attachment, row.item).then((file) => {
      openDocViewer({
        file,
        kind: docViewerKind(file.name, effectiveContent(file), attachment.mimeType),
        pageName: 'document_viewer',
      });
    });
  };

  const share = (row: FilesRow) => {
    logPageClickBi({ page_name: PAGE, button_name: 'share_file', answer: row.item.name });
    const attachment = attachmentOf(row);
    if (attachment) void resolvedFile(attachment, row.item).then(downloadArtifact);
  };

  const actionsFor = (row: FilesRow) => {
    const askLink = chatHandoffLink({ kind: 'new', message: t('agentCreations.detail.askMessage', { file: row.item.name }) }, basePath);
    const callbacks: FileActionCallbacks = {
      onView: () => open(row),
      onDiscuss: () => void router.push(askLink.route, askLink.path),
    };
    const kind = docViewerKind(row.item.name, effectiveContent(row.item), attachmentOf(row)?.mimeType);
    return buildFileActions(row.item, kind, callbacks, { context: 'row', t, agentName: agent.short }).map((group) =>
      group.map((action) => ({
        ...action,
        onSelect: () => {
          if (action.key !== 'view') {
            logPageClickBi({ page_name: PAGE, button_name: `file_action_${action.key}`, answer: row.item.name });
          }
          action.onSelect?.();
        },
      })),
    );
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <ScrollArea component={Card} className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <Typography variant="h3">{t('chatFiles.title')}</Typography>
          <IconButton aria-label={t('common.close')} variant="text" onClick={onClose}>
            <X size={20} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
          </IconButton>
        </div>
        {loading ? (
          <PageLoader />
        ) : (
          <FilesFlatList
            rows={rows}
            onOpen={open}
            onShare={share}
            // This glyph DOWNLOADS here — a conversation's file list grants nobody access (see the
            // allowlist in `test/file-share-action-wired.test.ts`), so it must not say "Share".
            shareLabelKey="files.share.title"
            actionsFor={actionsFor}
            onRowMenu={(row) => logPageClickBi({ page_name: PAGE, button_name: 'file_row_menu', answer: row.item.name })}
            onBi={(component) => logPageClickBi({ page_name: PAGE, button_name: component })}
            emptyCaption={t('chatFiles.empty')}
          />
        )}
      </ScrollArea>
    </div>
  );
}
